import {
  DOM_FLIGHTS_PARAMETERS,
  INTER_FLIGHTS_PARAMETERS,
  default_sort,
  getFlightDefaultDepartureDate,
  getToday
} from "@website/ticket-ui/common/constants";
import { FormattedQueryParameters } from "@website/ticket-ui/common/types";
import {
  CabinType,
  DepartureTime,
  IntFlightTrackingTypes,
  SearchSource,
  SortingOption,
  TripType
} from "@website/tracking/types";
import { daysBetweenDates } from "@website/utils/date-format";

export const CABIN_TYPE = {
  ECONOMY: CabinType["economy"],
  BUSINESS: CabinType["business"],
  FIRST_CLASS: CabinType["first_class"],
  PREMIUM_ECONOMY: CabinType["premium_economy"]
};

export const SORTING_TYPE = {
  best_price: SortingOption["best"],
  lowest_price: SortingOption["cheapest"],
  highest_price: SortingOption["most_expensive"],
  lowest_flight_duration: SortingOption["least_journey_duration"],
  earliest: SortingOption["earliest"],
  latest: SortingOption["latest"]
};
export const SEARCH_SOURCE = {
  searchBox: SearchSource["search_box"],
  recentSearch: SearchSource["recent_search"],
  price_table: SearchSource["price_table"],
  search_results_page: SearchSource["search_results_page"],
  search_expired: SearchSource["search_expired"]
};
export const DEPARTURE_TIME = {
  "all-day": DepartureTime["all_day"],
  midnight: DepartureTime["midnight"],
  noon: DepartureTime["noon"],
  morning: DepartureTime["morning"],
  night: DepartureTime["night"]
};

export const formatTripType = (isRoundTrip: boolean) =>
  isRoundTrip ? TripType["roundtrip"] : TripType["one_way"];
export const convertSearchParametersToTracking = (
  product: "dom-flight" | "inter-flight",
  searchParameters: FormattedQueryParameters,
  originCity: string,
  destinationCity: string,
  isOriginCity: boolean,
  isDestinationCity: boolean
): Partial<IntFlightTrackingTypes> => {
  const {
    adultCount,
    cabinType,
    childCount,
    dateType,
    departureDate,
    infantCount,
    selectedSolutionIndex,
    is_price_table_best_price,
    returnDate,
    order_by,
    source
  } = searchParameters ?? {};

  const filterParameters =
    product === "inter-flight"
      ? INTER_FLIGHTS_PARAMETERS
      : DOM_FLIGHTS_PARAMETERS;
  const filters = filterParameters.filter((key) =>
    Object.keys(searchParameters).includes(key)
  );
  const isRoundTrip = Boolean(searchParameters.returnDate);

  const departureDateFinal = departureDate
    ? new Date(departureDate)
    : new Date(getFlightDefaultDepartureDate(product));
  return {
    departure_date: departureDateFinal,
    cabin_type:
      CABIN_TYPE[cabinType as keyof typeof CABIN_TYPE] ?? CABIN_TYPE["ECONOMY"],
    origin_city: originCity,
    destination_city: destinationCity,
    origin_is_city: isOriginCity,
    destination_is_city: isDestinationCity,
    search_calendar_type: dateType,
    trip_type: formatTripType(isRoundTrip),
    route: `${originCity} - ${destinationCity}`,
    number_of_passengers:
      Number(adultCount) + Number(childCount) + Number(infantCount) || 1,
    search_source:
      SEARCH_SOURCE[source as keyof typeof SEARCH_SOURCE] ?? undefined,
    has_filters_on: filterParameters.some((parameter) =>
      Object.keys(searchParameters).includes(parameter)
    ),
    position_rank: Number(selectedSolutionIndex) || undefined,
    return_departure_date: returnDate ? new Date(returnDate) : undefined,
    origin_airport: isOriginCity ? "All Airport" : originCity,
    destination_airport: isDestinationCity ? "All Airport" : destinationCity,
    filters: filters.length > 0 ? filters : undefined,
    is_price_table_best_price: Object.keys(searchParameters).includes(
      "is_price_table_best_price"
    )
      ? is_price_table_best_price === "true"
      : undefined,
    sorting_option:
      SORTING_TYPE[order_by as keyof typeof SORTING_TYPE] ||
      SORTING_TYPE[default_sort.value as keyof typeof SORTING_TYPE],
    search_window: departureDateFinal
      ? daysBetweenDates(getToday(), departureDateFinal)
      : undefined,
    adult_count: Number(adultCount) || 1,
    child_count: Number(childCount || 0),
    infant_count: Number(infantCount || 0),
    utm_medium: "",
    utm_source: ""
  };
};
